import { useEffect, useState } from "react";
import { api, fmtBRL, fmtDate } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useLojistaAuth } from "@/context/AuthContext";
import { CalendarClock, FileText, TrendingUp } from "lucide-react";

export default function LojistaContrato() {
  const { lojista } = useLojistaAuth();
  const [contrato, setContrato] = useState(null);

  useEffect(() => {
    if (!lojista?.id) return;
    // Endpoint admin não é acessível — usamos os dados de /lojista/auth/me
    setContrato(lojista);
  }, [lojista]);

  if (!contrato) return <div className="text-muted-foreground">Carregando...</div>;

  const df = contrato.data_fim ? new Date(contrato.data_fim) : null;
  const diasRestantes = df ? Math.ceil((df - new Date()) / (1000 * 60 * 60 * 24)) : null;
  const alertaTermino = diasRestantes != null && diasRestantes < 90;

  return (
    <div className="space-y-6 fade-up">
      <div>
        <div className="label-tag mb-2">Seu contrato</div>
        <h1 className="font-heading font-black tracking-tighter text-3xl">Meu Contrato</h1>
      </div>

      <Card className="p-6 rounded-2xl border-border">
        <div className="flex items-center gap-3 mb-4">
          <FileText size={18} />
          <div>
            <div className="font-heading font-bold text-lg leading-tight">{contrato.razao_social}</div>
            <div className="text-xs font-mono text-muted-foreground">{contrato.cnpj}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-6">
          <div className="stat-block p-4 rounded-xl">
            <div className="label-tag">Aluguel base</div>
            <div className="stat-num text-2xl mt-2">{fmtBRL(contrato.valor_aluguel)}</div>
          </div>
          <div className="stat-block p-4 rounded-xl">
            <div className="label-tag">Vencimento</div>
            <div className="stat-num text-2xl mt-2">dia {contrato.dia_vencimento}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <div className="border border-border p-4 rounded-xl">
            <div className="label-tag">Início</div>
            <div className="text-sm font-semibold mt-1">{fmtDate(contrato.data_inicio)}</div>
          </div>
          <div className={`border p-4 rounded-xl ${alertaTermino ? "border-warning/40 bg-warning/5" : "border-border"}`}>
            <div className="label-tag flex items-center gap-1">
              <CalendarClock size={11} /> Término
            </div>
            <div className="text-sm font-semibold mt-1">{fmtDate(contrato.data_fim)}</div>
            {diasRestantes != null && (
              <div className={`text-[11px] font-mono mt-1 ${alertaTermino ? "text-warning font-bold" : "text-muted-foreground"}`}>
                {diasRestantes > 0 ? `${diasRestantes} dias restantes` : "Vencido"}
              </div>
            )}
          </div>
        </div>
      </Card>

      <Card className="p-5 rounded-2xl border-border">
        <div className="flex items-center gap-2 mb-3">
          <TrendingUp size={14} />
          <h2 className="font-heading font-bold text-base">Composição do boleto</h2>
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-4">
          Todo mês seu boleto é composto por três parcelas transparentes:
        </p>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between border-b border-border/60 pb-2">
            <span>Aluguel (contratado)</span>
            <span className="font-mono">{fmtBRL(contrato.valor_aluguel)}</span>
          </div>
          <div className="flex justify-between border-b border-border/60 pb-2">
            <span>Cota de rateio</span>
            <Badge variant="outline" className="text-[9px]">Variável mensal</Badge>
          </div>
          <div className="flex justify-between border-b border-border/60 pb-2">
            <span>IPTU (fração)</span>
            <Badge variant="outline" className="text-[9px]">Só estandes container</Badge>
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground mt-4 leading-relaxed">
          Energia é 100% individualizada e cobrada diretamente pela concessionária.
          Lojas têm IPTU próprio pago diretamente à prefeitura.
        </p>
      </Card>

      <Card className="p-5 rounded-2xl border-border">
        <h2 className="font-heading font-bold text-base mb-3">Responsável cadastrado</h2>
        <div className="space-y-1 text-sm">
          <div><span className="text-muted-foreground">Nome: </span>{contrato.responsavel}</div>
          <div><span className="text-muted-foreground">E-mail: </span>{contrato.email || "—"}</div>
          <div><span className="text-muted-foreground">WhatsApp: </span><span className="font-mono">{contrato.telefone}</span></div>
        </div>
      </Card>
    </div>
  );
}
